import { AxiosResponse } from "axios";

import {
  IAddPatientDeviceRequest,
  IAdminDeviceHistory,
  IAdminPatientDevice,
} from "../../@types/admin/AdminDevice";
import { api } from "../api";

/**
 * Patient devices (EN-5166), migrated from Retool's `moduleDevices`.
 *
 * Every write is keyed by the device's `identifier`, not by an id — that is
 * the module's contract, and it is why renaming an identifier is its own
 * endpoint rather than part of a general update.
 */
export const patientDevicesService = {
  /**
   * Devices. Omit `patientId` for every device — the Cellular Devices page
   * left Retool's module prop blank, the patient pages fill it in.
   */
  async getDevices(
    patientId?: number
  ): Promise<AxiosResponse<IAdminPatientDevice[]>> {
    return api.get<IAdminPatientDevice[]>("/Retool/PatientDevices", {
      params: patientId === undefined ? undefined : { patientId },
    });
  },

  /** A device's detail and patient history. */
  async getDeviceHistory(
    identifier: string
  ): Promise<AxiosResponse<IAdminDeviceHistory>> {
    return api.get<IAdminDeviceHistory>(
      `/Retool/PatientDevices/${encodeURIComponent(identifier)}/History`
    );
  },

  /** Registers a device to a patient. */
  async addDevice(
    request: IAddPatientDeviceRequest
  ): Promise<AxiosResponse<unknown>> {
    return api.post("/Retool/PatientDevices", request);
  },

  /**
   * ⚠️ Disconnects a device from its patient — their readings stop flowing.
   * Retool guarded this with a confirm dialog; keep the confirmation step.
   */
  async disconnectDevice(identifier: string): Promise<AxiosResponse<unknown>> {
    return api.delete(
      `/Retool/PatientDevices/${encodeURIComponent(identifier)}`
    );
  },

  /** Renames a device's identifier. */
  async updateIdentifier(
    identifier: string,
    newIdentifier: string
  ): Promise<AxiosResponse<unknown>> {
    return api.put(`/Retool/PatientDevices/${encodeURIComponent(identifier)}`, {
      newIdentifier,
    });
  },
};
